import { dragTagToCells } from "@/apis/table/route";
import { useTableId } from "@/components/mapping/hooks";
import { convertAddressToCoordinates } from "@/jspreadsheet/jspreadsheet";
import queries from "@/queries";
import { useMainStore } from "@/stores/main";
import { CellType, useMappingStore } from "@/stores/mapping";
import { useSpreadsheetStore } from "@/stores/spreadsheet";
import { Dimension } from "@/types/mapping";
import { CellsRange } from "@/types/spreadsheet";
import { getCellsFromRange } from "@/utils/cell";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkbookId } from "../use-workbook";

export const useDragTaggingMutationKey = (
  tableId: string | null,
  currentSelection: CellsRange | null,
) => {
  return ["drag-tag", tableId, currentSelection?.address];
};

export const useShouldDragTag = () => {
  const currentSelection = useMainStore((state) => state.selection);
  const worksheet = useSpreadsheetStore((state) => state.activeSheet);

  if (!currentSelection.current?.address) {
    return false;
  }

  const address = currentSelection.current.address;

  if (!address.includes(":")) {
    return false;
  }

  const merges = worksheet?.getMerge();

  if (!merges) {
    return true;
  }

  const [startCell, endCell] = address.split(":");
  const selectionStart = convertAddressToCoordinates(startCell);
  const selectionEnd = convertAddressToCoordinates(endCell);

  for (const [mergeStartCell, [colSpan, rowSpan]] of Object.entries(merges)) {
    const mergeStart = convertAddressToCoordinates(mergeStartCell);

    const mergeEnd = {
      col: mergeStart.col + colSpan - 1,
      row: mergeStart.row + rowSpan - 1,
    };

    if (
      selectionStart.col === mergeStart.col &&
      selectionStart.row === mergeStart.row &&
      selectionEnd.col === mergeEnd.col &&
      selectionEnd.row === mergeEnd.row
    ) {
      return false;
    }
  }

  return true;
};

export default function useDragTagging(
  options?: {
    onSuccess?: () => void;
    onError?: () => void;
  },
  dimensionOverride?: Dimension,
) {
  const { data: workbookId } = useWorkbookId();

  const { data: tableId } = useTableId();

  const selectedFact = useMappingStore((state) => state.fact.selected);
  const currentSelection = useMainStore((state) => state.selection);
  const storeDimension = useMappingStore((state) => state.dimension.dimension);
  // 후보 적용처럼 현재 탭과 다른 차원의 태그를 드래그할 때 차원을 명시 주입한다.
  const dimension = dimensionOverride ?? storeDimension;
  const queryClient = useQueryClient();
  const { data: cells } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId?.tableId!)
      ._ctx.cells(),
    enabled: !!workbookId && !!tableId?.tableId,
    select: (data) => {
      return data.cells.filter(
        (cell) => cell.type === CellType.DATA || cell.type === CellType.SUM,
      );
    },
  });

  const mutationKey = useDragTaggingMutationKey(
    tableId?.tableId,
    currentSelection.current,
  );

  const { mutate } = useMutation({
    mutationKey: mutationKey,
    mutationFn: async ({
      tagKey,
      axisKey,
    }: {
      tagKey: string;
      axisKey?: string | null;
    }) => {
      if (!workbookId) {
        throw new Error("Workbook ID is not found");
      }
      if (!tableId?.tableId) {
        throw new Error("Table ID is not found");
      }
      if (!selectedFact?.roleKey) {
        throw new Error("Selected fact role key is not found");
      }
      if (!cells) {
        throw new Error("Cells not found");
      }

      if (!currentSelection.current?.address) {
        throw new Error("Current selection is not found");
      }

      const cellAddresses = getCellsFromRange(
        currentSelection.current.address,
      ).filter((cell) => {
        return cells?.find((c) => c.address.split(":")[0] === cell);
      });

      const response = await dragTagToCells({
        workbookId: workbookId,
        tableId: tableId.tableId,
        request: {
          tagKey: tagKey,
          dimension: dimension,
          cellAddresses: cellAddresses,
          axisKey: axisKey,
        },
      });

      queryClient.refetchQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(tableId?.tableId!)
          ._ctx.cells().queryKey,
      });

      return response;
    },
    onSuccess: (data) => {
      options?.onSuccess?.();
      if (!workbookId || !tableId?.tableId) return;

      const tableCtx = queries
        .workbook(workbookId)
        ._ctx.tables(tableId.tableId);

      Object.keys(data.cells).forEach((cellAddress) => {
        const normalizedCellAddress = cellAddress.includes(":")
          ? cellAddress.split(":")[0]
          : cellAddress;

        queryClient.invalidateQueries({
          queryKey: tableCtx._ctx.cells(normalizedCellAddress)._ctx.tags()
            .queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: tableCtx._ctx
            .taxonomy()
            ._ctx.tags(dimension, normalizedCellAddress).queryKey,
        });
      });

      queryClient.invalidateQueries({
        queryKey: tableCtx._ctx.editablePreview().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: tableCtx._ctx.editableTreeView().queryKey,
      });
    },
    onError: (error) => {
      // 여기서 나는 에러는 전부 내부 불변식 위반이라 사용자에게 보여줄 문장이 아니다.
      // 무엇이 실패했는지 아는 건 호출자뿐이므로 사용자 알림은 onError 로 위임한다.
      Sentry.captureException(error);
      options?.onError?.();
    },
  });
  return { mutate };
}
